/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — "Card style" toolbar popover (People Rebuild §3
   "Node customization [granted]"): shape (rectangle/circle),
   density (expanded/compact), badge visibility toggles. Portal'd,
   viewport-clamped, dismisses on mousedown-outside + Esc, per
   DESIGN.md §4 menu law.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LayoutGrid, ChevronDown } from 'lucide-react';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import type { NodeStylePrefs } from './types';
import { appLayerProps } from '../../../components/ui/app-layer';

export function CardStylePopover({
  value,
  onChange,
}: {
  value: NodeStylePrefs;
  onChange: (patch: Partial<NodeStylePrefs>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, left: r.left });
    };
    reposition();
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const rowLabel: React.CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--tm)',
    fontWeight: 600,
    marginBottom: 'var(--space-1)',
    display: 'block',
  };
  const segBtn = (active: boolean): React.CSSProperties => ({
    flex: 1,
    minHeight: rem(28),
    padding: 'var(--space-1) var(--space-2)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: active ? 600 : 500,
    background: active ? 'var(--ac)' : 'var(--sf)',
    color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
    border: '1px solid var(--bd)',
    cursor: 'pointer',
  });

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        title="Change how person cards look (shape, density, badges)"
        aria-label="Card style options"
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: rem(6),
          padding: rem(6) + ' var(--space-3)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 500,
          background: 'var(--sf)',
          color: 'var(--tx)',
          border: '1px solid var(--bd)',
          borderRadius: rem(8),
          cursor: 'pointer',
        }}
      >
        <Icon icon={LayoutGrid} em={1.1818} aria-hidden="true" />
        Card style
        <Icon icon={ChevronDown} em={1.0909} aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            {...appLayerProps}
            ref={panelRef}
            data-orgchart-menu
            role="dialog"
            aria-label="Card style options"
            style={{
              position: 'fixed',
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              visibility: coords ? 'visible' : 'hidden',
              zIndex: 1000,
              width: rem(220),
              background: 'var(--sa)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              padding: rem(10),
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            }}
          >
            <div style={{ marginBottom: rem(10) }}>
              <span style={rowLabel}>Shape</span>
              <div
                style={{ display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}
              >
                <button
                  style={segBtn(value.shape === 'rectangle')}
                  onClick={() => onChange({ shape: 'rectangle' })}
                >
                  Rectangle
                </button>
                <button
                  style={segBtn(value.shape === 'circle')}
                  onClick={() => onChange({ shape: 'circle' })}
                >
                  Circle
                </button>
              </div>
            </div>
            <div style={{ marginBottom: rem(10) }}>
              <span style={rowLabel}>Density</span>
              <div
                style={{ display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}
              >
                <button
                  style={segBtn(value.density === 'expanded')}
                  onClick={() => onChange({ density: 'expanded' })}
                >
                  Expanded
                </button>
                <button
                  style={segBtn(value.density === 'compact')}
                  onClick={() => onChange({ density: 'compact' })}
                >
                  Compact
                </button>
              </div>
            </div>
            <div>
              <span style={rowLabel}>Badges</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                {(
                  [
                    ['showDepartmentPill', 'Department pill'],
                    ['showTeamPills', 'Team pills'],
                    ['showTitleBadge', 'Title badge'],
                    ['showLeaderStar', 'Leader star'],
                  ] as [keyof NodeStylePrefs, string][]
                ).map(([key, label]) => (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rem(6),
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(value[key])}
                      onChange={(e) =>
                        onChange({ [key]: e.target.checked } as Partial<NodeStylePrefs>)
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
